import React, { FC } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';

import CloseButton from 'components/CustomButtons/CloseButton';
import {
  getCustomer,
  getIsSpecialPaymentMenuSelected,
} from 'reducers/Payments';
import { ExternalPaymentActions } from 'containers/Forms/IntegrationUIComponents/External/External';
import { getCheckBehavior } from 'reducers/configs/settings';
import Currencies from 'containers/Forms/Payment/components/AdditionalCurrency';
import { setPaymentSelected } from 'actions/Payments';
import { getActivePaymentIntegration } from 'reducers/cafaConfigs';

import { PaymentActions, PaymentContent } from '../../components';

import classes from './PaymentDesktop.module.scss';

type Props = {
  voidHandler: () => void;
};

const PaymentDesktop: FC<Props> = ({ voidHandler }) => {
  const { t } = useTranslation('payment');

  const selectedIntegration = useSelector(getActivePaymentIntegration);

  const checkBehaviour = useSelector(getCheckBehavior);

  const {
    companyName,
    firstName,
    lastName,
    availableCredit,
    fullName,
  } = useSelector(getCustomer);

  const useStoreCredit = availableCredit && availableCredit > 0;
  const credit = useStoreCredit && availableCredit.toFixed(2);

  const getName = () => {
    if (companyName) {
      return companyName;
    }
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    return fullName;
  };

  const name = getName();

  const title = t('title', {
    context: useStoreCredit ? 'credit' : undefined,
    name,
    credit,
  });

  const dispatch = useDispatch();
  const exitGiftcard = () => dispatch(setPaymentSelected(''));

  const {
    isGiftCardMenuSelected,
    isCheckMenuSelected,
    isCustomPaymentTypeMenuSelected,
  } = useSelector(getIsSpecialPaymentMenuSelected);
  const isDefaultPaymentView =
    !isGiftCardMenuSelected &&
    !(checkBehaviour === 'OLD' && isCheckMenuSelected) &&
    !isCustomPaymentTypeMenuSelected;

  return (
    <div data-testid="payment-desktop" className={classes['payment-desktop']}>
      <Modal.Header>
        <Modal.Title>
          <span data-testid="payment-desktop-title" className="h4">
            {title}
            <Currencies />
          </span>
        </Modal.Title>
        <CloseButton
          action={isGiftCardMenuSelected ? exitGiftcard : voidHandler}
        />
      </Modal.Header>

      <Modal.Body>
        <Container>
          <Row className="no-gutters">
            <PaymentContent />
          </Row>
        </Container>
      </Modal.Body>
      {isDefaultPaymentView && (
        <Modal.Footer className="flex-column">
          <PaymentActions voidHandler={voidHandler} />
          {selectedIntegration === 'external' && <ExternalPaymentActions />}
        </Modal.Footer>
      )}
    </div>
  );
};

export default PaymentDesktop;
